import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";
import { useCognitoAuth } from "@/hooks/useCognitoAuth";
import { GUIDES_PATH, isGuidesEnabled } from "@/lib/guidesFlag";
import { useHostname } from "@/lib/ssr";
import { cn } from "@/lib/utils";
import { EAGLE_DARK } from "./images";
import { APP_PATH, APP_WAITLIST, DOWNLOAD_PATH, FIND_PATH, LOGIN_PATH, REGISTER_PATH, STUDIO_PATH, STUDIO_PRICING } from "./paths";
import { PRODUCT_NAME, PRODUCT_WORDMARK, type Product } from "./products";
import { BTN_PRIMARY_MD, BTN_PRIMARY_SM, FOCUS } from "./ui";

/** The phone menu's id (the Menu button controls it). */
export const NAV_ID = "home-nav";

/** Which product a page belongs to (the home page is the app's: photohawk.com leads with Photohawk, the app; owner,
 *  21 Oct 2026): the wordmark names it and the header button is its next step. */
export type SitePath = "home" | "studio" | "app";

const productOf = (path: SitePath): Product => (path === "studio" ? "studio" : "personal");

/** Every variant in one grid cell, only the current one visible: the box is always as wide as the widest, so
 *  switching between Personal and Studio never moves the header (or the page under it). */
const Stacked = ({ current, variants }: { current: string; variants: Record<string, ReactNode> }) => (
  <span className="inline-grid">
    {Object.entries(variants).map(([key, node]) => (
      <span key={key} className={`[grid-area:1/1] ${key === current ? "" : "invisible"}`} aria-hidden={key === current ? undefined : true}>
        {node}
      </span>
    ))}
  </span>
);

/** A label's rough width in Latin characters (CJK counts double), to keep the phone header on one row. */
const wide = (c: string) => {
  const p = c.codePointAt(0) ?? 0;
  return (p >= 0x3000 && p <= 0x9fff) || (p >= 0xff00 && p <= 0xffef);
};
const labelWidth = (s: string) => [...s].reduce((n, c) => n + (wide(c) ? 2 : 1), 0);
/** The longest primary-button label that still fits beside the wordmark and the Menu button on a 360 px phone. */
const PHONE_BUTTON_MAX = 18;

// The dashboard's nav item (src/components/workspace/WorkspaceSidebar.tsx): muted text, an amber wash on hover, and
// the active page in the darker amber on that wash. Never a weight change, so nothing moves when it changes.
const NAV_ITEM = `inline-flex h-9 items-center rounded-lg px-3 text-sm font-medium text-muted-foreground no-underline transition-colors hover:bg-accent hover:text-foreground aria-[current=page]:bg-accent aria-[current=page]:text-accent-foreground ${FOCUS}`;
// The phone menu's rows: plain text, full width, 48 px tall.
const ROW = `flex min-h-12 w-full items-center justify-between gap-3 rounded-lg px-3 text-base font-medium text-foreground no-underline transition-colors hover:bg-accent aria-[current=page]:bg-accent aria-[current=page]:text-accent-foreground ${FOCUS}`;

/**
 * The one header of the new site (home, /studio, /personal, /personal/*, /download), in the dashboard's light look
 * (white, a 1 px border under it, as the admin's header). The wordmark is "Photohawk" on the app's pages and the home
 * page, and "Photohawk Studio" on Studio's. The links, the same everywhere (owner, 21 Oct 2026): The app · Find your
 * event photos · Guides (where they're on) · Photohawk Studio, plus Pricing on Studio's pages. Then the language;
 * "Studio log in" (a web account is only ever Studio: the app signs in inside the app); and one primary button: "Get
 * the app" on the home page, "Join the waitlist" on the app's page, "Start Studio free" on Studio's, so nobody signs
 * up for the wrong product. Text only: no icons in the links.
 *
 * Layout (36 px controls): one row from 1280 px; from 768 px the logo and actions, with the links below; on a
 * phone the wordmark, the main button (where it fits) and a Menu button that opens a plain list of the same links,
 * then the log-in, the language and the main button.
 */
const HomeHeader = ({ path }: { path: SitePath }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useCognitoAuth();
  const guides = isGuidesEnabled(useHostname());
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const product = productOf(path);

  // A link in the menu was followed: close it.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // A product link is highlighted only on that product's own pages: the home page and the shared pages (pricing,
  // blog, legal, Find your photos) highlight neither.
  const inProduct = (p: Product) =>
    p === "studio"
      ? pathname === STUDIO_PATH || pathname === DOWNLOAD_PATH
      : pathname === APP_PATH || pathname.startsWith(`${APP_PATH}/`);
  const productHref = (p: Product) => (p === "studio" ? STUDIO_PATH : APP_PATH);

  const startLabel = t("home.nav.startStudio");
  const waitlistLabel = t("home.joinWaitlist");
  const getAppLabel = t("home.nav.getApp");
  // The page's next step: Studio's pages start Studio (nothing for a signed-in Studio user); the app's own page joins
  // the waitlist until the App Store listing is live; the home page goes to the app.
  const primaryKind = product === "studio" ? "start" : path === "app" ? "waitlist" : "app";
  // Every label shares one box, so the button keeps its width when moving between the app's pages and Studio's.
  const primaryLabel = <Stacked current={primaryKind} variants={{ start: startLabel, waitlist: waitlistLabel, app: getAppLabel }} />;
  const primaryTo = primaryKind === "start" ? (isAuthenticated ? null : REGISTER_PATH) : primaryKind === "waitlist" ? APP_WAITLIST : APP_PATH;
  // On a phone the header button shows from 360 px where its label is short enough to keep the header on one row
  // (it's always in the menu, and in each page's hero).
  const phoneButton = Math.max(labelWidth(startLabel), labelWidth(waitlistLabel), labelWidth(getAppLabel)) <= PHONE_BUTTON_MAX;
  const primary = primaryTo && (
    <Link
      to={primaryTo}
      className={cn(BTN_PRIMARY_SM, "px-3 text-[13px] shadow-sm sm:px-4 sm:text-sm", phoneButton ? "hidden min-[360px]:inline-flex" : "hidden md:inline-flex")}
      data-testid="header-primary"
    >
      {primaryLabel}
    </Link>
  );

  const logIn = isAuthenticated ? (
    <Link to="/admin/workspaces" className={NAV_ITEM}>
      {t("nav.dashboard")}
    </Link>
  ) : (
    <Link to={LOGIN_PATH} className={`${NAV_ITEM} whitespace-nowrap`} data-testid="header-login">
      {t("home.nav.studioLogIn")}
    </Link>
  );

  // The links, the same on every page: The app · Find your event photos · Guides · Photohawk Studio (owner, 21 Oct
  // 2026); Studio's pages add Pricing (only Studio has plans on the web).
  const productLabel = (p: Product) => (p === "studio" ? PRODUCT_NAME.studio : t("home.nav.app"));
  const productLink = (p: Product) => (
    <Link to={productHref(p)} aria-current={inProduct(p) ? "page" : undefined} className={NAV_ITEM} data-testid={`nav-${p}`}>
      {productLabel(p)}
    </Link>
  );
  const studioPages = product === "studio";
  return (
    <header className="border-b border-border bg-background text-foreground" data-testid="home-header">
      <div className="mx-auto flex max-w-[1320px] flex-wrap items-center gap-x-2 gap-y-1 px-[clamp(16px,4vw,56px)] py-2.5 sm:gap-x-3">
        {/* The wordmark: "Photohawk" and the product, stacked on a phone, one line from 640 px. */}
        <Link to="/" aria-label={t("home.homeLink")} className={`order-1 flex shrink-0 items-center gap-2 rounded-lg text-foreground no-underline ${FOCUS}`} data-testid="header-wordmark">
          <img src={EAGLE_DARK} alt="" width={30} height={32} className="h-8 w-[30px] object-contain" />
          <span className="flex flex-col font-display font-bold leading-none tracking-[-0.02em] sm:flex-row sm:items-baseline sm:gap-[0.3em] sm:text-xl">
            <span className="text-[17px] sm:text-xl" data-testid="header-photohawk">
              Photohawk
            </span>
            {/* The app is just "Photohawk"; Studio's pages add "Studio". */}
            {product === "studio" && (
              <span className="mt-[3px] text-[13px] font-semibold tracking-normal text-accent-foreground sm:mt-0 sm:text-xl sm:font-bold sm:tracking-[-0.02em]" data-testid="header-product">
                {PRODUCT_WORDMARK.studio}
              </span>
            )}
          </span>
        </Link>

        {/* From 768 px: the links, as text. */}
        <nav
          aria-label={t("home.mainNav")}
          className="order-4 hidden w-full flex-wrap items-center gap-x-1 md:-ml-3 md:flex xl:order-2 xl:ml-6 xl:w-auto"
          data-testid="desktop-nav"
        >
          {productLink("personal")}
          <Link to={FIND_PATH} className={NAV_ITEM} data-testid="nav-find">
            {t("home.nav.findEventPhotos")}
          </Link>
          {guides && (
            <Link to={GUIDES_PATH} className={NAV_ITEM}>
              {t("footer.guides")}
            </Link>
          )}
          {productLink("studio")}
          {studioPages && (
            <Link to={STUDIO_PRICING} className={NAV_ITEM} data-testid="nav-pricing">
              {t("nav.pricing")}
            </Link>
          )}
        </nav>

        <div className="order-2 ml-auto flex items-center gap-2 xl:order-3">
          <span className="hidden md:block">
            <LanguageSelector
              triggerClassName={`inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${FOCUS}`}
            />
          </span>
          <span className="hidden md:block">{logIn}</span>
          {primary}
          <button
            type="button"
            aria-expanded={open}
            aria-controls={NAV_ID}
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-accent md:hidden ${FOCUS}`}
            data-testid="menu-button"
          >
            <span className="sr-only">{t("home.nav.menu")}</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {open ? <path d="M6 6l12 12M18 6 6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>

        {/* The phone menu: plain text rows in three groups, no icons. */}
        {open && (
          <nav id={NAV_ID} aria-label={t("home.mainNav")} className="order-5 flex w-full flex-col pb-3 pt-1 md:hidden" data-testid="phone-menu">
            <div className="flex flex-col gap-0.5 border-t border-border pt-2">
              <Link to={productHref("personal")} aria-current={inProduct("personal") ? "page" : undefined} className={ROW} data-testid="menu-personal">
                {productLabel("personal")}
              </Link>
              <Link to={FIND_PATH} className={ROW}>
                {t("home.nav.findEventPhotos")}
              </Link>
              {guides && (
                <Link to={GUIDES_PATH} className={ROW}>
                  {t("footer.guides")}
                </Link>
              )}
              <Link to={productHref("studio")} aria-current={inProduct("studio") ? "page" : undefined} className={ROW} data-testid="menu-studio">
                {productLabel("studio")}
              </Link>
              {studioPages && (
                <Link to={STUDIO_PRICING} className={ROW} data-testid="menu-pricing">
                  {t("nav.pricing")}
                </Link>
              )}
            </div>
            <div className="mt-2 flex flex-col gap-0.5 border-t border-border pt-2">
              {isAuthenticated ? (
                <Link to="/admin/workspaces" className={ROW}>
                  {t("nav.dashboard")}
                </Link>
              ) : (
                <Link to={LOGIN_PATH} className={ROW} data-testid="menu-login">
                  {t("home.nav.studioLogIn")}
                </Link>
              )}
              <LanguageSelector triggerClassName={`${ROW} border-0 bg-transparent text-left`} rowLabel={t("home.nav.language")} />
              {primaryTo && (
                <Link to={primaryTo} className={cn(BTN_PRIMARY_MD, "mt-2 h-12 w-full text-base")} data-testid="menu-primary">
                  {primaryKind === "start" ? startLabel : primaryKind === "waitlist" ? waitlistLabel : getAppLabel}
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default HomeHeader;
